import React, { useState, useMemo, useEffect } from 'react';
import './ForecastDashboard.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

const ForecastDashboard = () => {
  // Hard-coded financial baseline
  const baselineDate = '2025-12-01'; // Baseline date for forecast start
  const startingCash = 350000; // As of Dec 1, 2025
  const lineOfCreditLimit = 500000; // $500k line of credit for inventory
  const burn2025Monthly = 10000; // $150k/year = ~$10k/month for 2025
  const burn2026Annual = 200000; // $200k/year for 2026+

  // Financial inputs (default to zero for fresh start)
  const [leonSalary, setLeonSalary] = useState(100000);
  const [toolingCost2026, setToolingCost2026] = useState(0);
  const [toolingCost2027, setToolingCost2027] = useState(0);
  const [toolingCost2028, setToolingCost2028] = useState(0);
  const [cogsPerUnit, setCogsPerUnit] = useState(0);

  // Revenue inputs (default to zero except date)
  const [averagePrice, setAveragePrice] = useState(0);
  const [units2026, setUnits2026] = useState(0);
  const [units2027, setUnits2027] = useState(0);
  const [units2028, setUnits2028] = useState(0);
  const [revenueStartDate, setRevenueStartDate] = useState('2026-03-01');

  // Annual costs (controllable burn rates, default to zero)
  const [annualCost2026, setAnnualCost2026] = useState(0);
  const [annualCost2027, setAnnualCost2027] = useState(0);
  const [annualCost2028, setAnnualCost2028] = useState(0);

  // Saved views state
  const [savedViews, setSavedViews] = useState([]);
  const [viewName, setViewName] = useState('');
  const [selectedView, setSelectedView] = useState('');

  // Load saved views from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('forecastViews');
    let views = [];

    if (stored) {
      views = JSON.parse(stored);
    }

    // Define the default Assumptions view
    const assumptionsView = {
      name: 'Assumptions',
      timestamp: new Date().toISOString(),
      data: {
        leonSalary: 100000,
        toolingCost2026: 45000,
        toolingCost2027: 20000,
        toolingCost2028: 20000,
        cogsPerUnit: 75,
        averagePrice: 150,
        units2026: 1500,
        units2027: 3000,
        units2028: 5000,
        revenueStartDate: '2026-03-01',
        annualCost2026: 200000,
        annualCost2027: 200000,
        annualCost2028: 200000
      }
    };

    // Check if "Assumptions" view exists
    const assumptionsIndex = views.findIndex(v => v.name === 'Assumptions');
    if (assumptionsIndex >= 0) {
      // Update existing Assumptions view with latest data
      views[assumptionsIndex] = assumptionsView;
    } else {
      // Add Assumptions view at the beginning
      views = [assumptionsView, ...views];
    }

    localStorage.setItem('forecastViews', JSON.stringify(views));
    setSavedViews(views);
  }, []);

  // Save current state as a view
  const saveView = (nameToSave = null) => {
    const name = nameToSave || viewName;

    if (!name || !name.trim()) {
      alert('Please enter a name for this view');
      return;
    }

    const view = {
      name: name.trim(),
      timestamp: new Date().toISOString(),
      data: {
        leonSalary,
        toolingCost2026,
        toolingCost2027,
        toolingCost2028,
        cogsPerUnit,
        averagePrice,
        units2026,
        units2027,
        units2028,
        revenueStartDate,
        annualCost2026,
        annualCost2027,
        annualCost2028
      }
    };

    const updated = [...savedViews, view];
    setSavedViews(updated);
    localStorage.setItem('forecastViews', JSON.stringify(updated));
    setViewName('');
    setSelectedView(name.trim());
    alert(`View "${name.trim()}" saved successfully!`);
  };

  // Load a saved view
  const loadView = (viewName) => {
    const view = savedViews.find(v => v.name === viewName);
    if (view) {
      const d = view.data;
      setLeonSalary(d.leonSalary);
      setToolingCost2026(d.toolingCost2026 || d.toolingCost || 45000);
      setToolingCost2027(d.toolingCost2027 || 20000);
      setToolingCost2028(d.toolingCost2028 || 20000);
      setCogsPerUnit(d.cogsPerUnit);
      setAveragePrice(d.averagePrice);
      setUnits2026(d.units2026);
      setUnits2027(d.units2027);
      setUnits2028(d.units2028);
      setRevenueStartDate(d.revenueStartDate);
      setAnnualCost2026(d.annualCost2026);
      setAnnualCost2027(d.annualCost2027);
      setAnnualCost2028(d.annualCost2028);
      setSelectedView(viewName);
    }
  };

  // Delete a saved view
  const deleteView = (viewName) => {
    // Prevent deletion of the Assumptions view
    if (viewName === 'Assumptions') {
      alert('The "Assumptions" view cannot be deleted.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the view "${viewName}"?`)) {
      const updated = savedViews.filter(v => v.name !== viewName);
      setSavedViews(updated);
      localStorage.setItem('forecastViews', JSON.stringify(updated));
      if (selectedView === viewName) {
        setSelectedView('');
      }
    }
  };

  // Load a view from JSON
  const loadViewFromJSON = () => {
    const jsonString = window.prompt('Paste the JSON view data:');
    if (!jsonString || !jsonString.trim()) {
      return;
    }

    try {
      const viewData = JSON.parse(jsonString);

      // Validate the JSON structure
      if (!viewData.name || !viewData.data) {
        alert('Invalid JSON format. Must include "name" and "data" fields.');
        return;
      }

      // Check if view with this name already exists
      const existingIndex = savedViews.findIndex(v => v.name === viewData.name);
      let updated;

      if (existingIndex >= 0) {
        if (!window.confirm(`A view named "${viewData.name}" already exists. Do you want to replace it?`)) {
          return;
        }
        updated = [...savedViews];
        updated[existingIndex] = viewData;
      } else {
        updated = [...savedViews, viewData];
      }

      // Save to localStorage and state
      setSavedViews(updated);
      localStorage.setItem('forecastViews', JSON.stringify(updated));

      // Load the view data into the form
      const d = viewData.data;
      setLeonSalary(d.leonSalary);
      setToolingCost2026(d.toolingCost2026 || d.toolingCost || 45000);
      setToolingCost2027(d.toolingCost2027 || 20000);
      setToolingCost2028(d.toolingCost2028 || 20000);
      setCogsPerUnit(d.cogsPerUnit);
      setAveragePrice(d.averagePrice);
      setUnits2026(d.units2026);
      setUnits2027(d.units2027);
      setUnits2028(d.units2028);
      setRevenueStartDate(d.revenueStartDate);
      setAnnualCost2026(d.annualCost2026);
      setAnnualCost2027(d.annualCost2027);
      setAnnualCost2028(d.annualCost2028);

      // Set the selected view
      setSelectedView(viewData.name);

      alert(`View "${viewData.name}" loaded successfully!`);
    } catch (error) {
      alert('Failed to parse JSON. Please check the format and try again.');
      console.error('JSON parse error:', error);
    }
  };

  // Export PDF with screenshot and table data
  const handleExport = async () => {
    try {
      // Capture the dashboard screenshot
      const dashboard = document.querySelector('.dashboard');
      if (!dashboard) {
        alert('Dashboard not found');
        return;
      }

      const canvas = await html2canvas(dashboard, {
        scale: 1.5,
        logging: false,
        useCORS: true,
        backgroundColor: '#f5f5f5'
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Add screenshot to first page
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 277; // A4 landscape width minus margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addPage();
      pdf.setPage(1);
      pdf.text('The Looey Forecast - Dashboard', 148.5, 15, { align: 'center' });
      pdf.addImage(imgData, 'PNG', 10, 25, imgWidth, Math.min(imgHeight, 180));

      // Add table data to second page
      pdf.addPage();
      pdf.setPage(2);
      pdf.setFontSize(16);
      pdf.text('Quarterly Forecast Data', 148.5, 15, { align: 'center' });

      // Prepare table data
      const tableHeaders = [
        'Period',
        'Start',
        'End',
        'Units',
        'Revenue',
        'COGS',
        'Gross Profit',
        'Op. Exp.',
        'One-Time',
        'Total Exp.',
        'Net CF',
        'Ending Cash',
        'LOC Balance'
      ];

      const tableRows = forecastData.map(item => [
        item.period,
        item.startDate,
        item.endDate,
        item.units.toLocaleString(),
        '$' + item.revenue.toLocaleString(),
        '$' + item.cogs.toLocaleString(),
        '$' + item.grossProfit.toLocaleString(),
        '$' + item.operatingExpenses.toLocaleString(),
        '$' + item.oneTimeExpenses.toLocaleString(),
        '$' + item.totalExpenses.toLocaleString(),
        '$' + item.netCashFlow.toLocaleString(),
        '$' + item.endingCash.toLocaleString(),
        '$' + item.lineOfCreditBalance.toLocaleString()
      ]);

      // Add table using autotable
      autoTable(pdf, {
        head: [tableHeaders],
        body: tableRows,
        startY: 25,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [26, 58, 82], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 10, right: 10 }
      });

      // Add settings JSON to third page
      pdf.addPage();
      pdf.setPage(3);
      pdf.setFontSize(16);
      pdf.text('View Settings (JSON)', 148.5, 15, { align: 'center' });

      const settingsJSON = {
        name: selectedView || 'Current Settings',
        timestamp: new Date().toISOString(),
        data: {
          leonSalary,
          toolingCost2026,
          toolingCost2027,
          toolingCost2028,
          cogsPerUnit,
          averagePrice,
          units2026,
          units2027,
          units2028,
          revenueStartDate,
          annualCost2026,
          annualCost2027,
          annualCost2028
        }
      };

      const jsonString = JSON.stringify(settingsJSON, null, 2);
      pdf.setFontSize(10);
      pdf.setFont('courier');

      // Split JSON into lines and add to PDF
      const lines = pdf.splitTextToSize(jsonString, 267);
      pdf.text(lines, 15, 30);

      // Download PDF
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      pdf.save(`looey-forecast-${timestamp}.pdf`);

    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Calculate forecast data with cash flow
  const forecastData = useMemo(() => {
    const data = [];
    let cumulativeCash = startingCash;
    let lineOfCreditBalance = lineOfCreditLimit; // Start with full credit available
    const revenueStart = new Date(revenueStartDate);

    // Calculate monthly burn for 2026+ (including Leon's salary)
    const monthlyBurn2026 = burn2026Annual / 12;

    // Start from Dec 1, 2025 and go forward
    let currentDate = new Date(baselineDate);

    for (let quarterIndex = 0; quarterIndex < 12; quarterIndex++) {
      // Calculate the start of each quarter
      const quarterStart = new Date(currentDate);

      // Calculate the end of each quarter (3 months later, minus 1 day)
      const quarterEnd = new Date(quarterStart);
      quarterEnd.setMonth(quarterEnd.getMonth() + 3);
      quarterEnd.setDate(quarterEnd.getDate() - 1);

      const year = quarterStart.getFullYear();
      const quarterNum = Math.floor((quarterStart.getMonth()) / 3) + 1;

      // Check if this quarter is after revenue start date
      const hasRevenue = quarterStart >= revenueStart;

      // Determine units for this year (divided by 4 for quarterly)
      let yearlyUnits = 0;
      if (year === 2026) yearlyUnits = units2026;
      else if (year === 2027) yearlyUnits = units2027;
      else if (year === 2028) yearlyUnits = units2028;

      const quarterlyUnits = yearlyUnits / 4;

      // Revenue calculations (only if after revenue start date)
      const revenue = hasRevenue ? quarterlyUnits * averagePrice : 0;
      const cogs = hasRevenue ? quarterlyUnits * cogsPerUnit : 0;
      const grossProfit = revenue - cogs;

      // Line of credit: Buy ALL year's inventory upfront in Q1 of each year
      // Then repay as we sell throughout the year
      let inventoryPurchase = 0;
      if (quarterNum === 1) {
        // In Q1, buy the entire year's worth of inventory
        let yearlyUnitsForInventory = 0;
        if (year === 2026) yearlyUnitsForInventory = units2026;
        else if (year === 2027) yearlyUnitsForInventory = units2027;
        else if (year === 2028) yearlyUnitsForInventory = units2028;

        inventoryPurchase = yearlyUnitsForInventory * cogsPerUnit;
        lineOfCreditBalance -= inventoryPurchase; // Borrow for full year
      }

      // Repay line of credit from this quarter's sales (the COGS portion)
      lineOfCreditBalance += cogs;

      // Expense calculations - determine burn rate based on year
      let quarterlyBurn;
      if (year === 2025) {
        // For 2025, use $10k/month burn
        quarterlyBurn = burn2025Monthly * 3;
      } else if (year === 2026) {
        quarterlyBurn = annualCost2026 / 4;
      } else if (year === 2027) {
        quarterlyBurn = annualCost2027 / 4;
      } else if (year === 2028) {
        quarterlyBurn = annualCost2028 / 4;
      } else {
        // Default for any year beyond 2028
        quarterlyBurn = monthlyBurn2026 * 3;
      }

      // Add tooling cost in first quarter of each year
      let oneTimeExpenses = 0;
      if (quarterNum === 1) {
        if (year === 2026) oneTimeExpenses = toolingCost2026;
        else if (year === 2027) oneTimeExpenses = toolingCost2027;
        else if (year === 2028) oneTimeExpenses = toolingCost2028;
      }
      const totalExpenses = quarterlyBurn + oneTimeExpenses;

      // Cash flow
      const netCashFlow = grossProfit - totalExpenses;
      cumulativeCash += netCashFlow;

      data.push({
        period: `Q${quarterIndex + 1}`,
        year: year,
        quarter: quarterNum,
        startDate: `${quarterStart.getMonth() + 1}/${quarterStart.getDate()}/${quarterStart.getFullYear().toString().slice(-2)}`,
        endDate: `${quarterEnd.getMonth() + 1}/${quarterEnd.getDate()}/${quarterEnd.getFullYear().toString().slice(-2)}`,
        units: hasRevenue ? Math.round(quarterlyUnits) : 0,
        revenue: Math.round(revenue),
        cogs: Math.round(cogs),
        grossProfit: Math.round(grossProfit),
        operatingExpenses: Math.round(quarterlyBurn),
        oneTimeExpenses: Math.round(oneTimeExpenses),
        totalExpenses: Math.round(totalExpenses),
        netCashFlow: Math.round(netCashFlow),
        endingCash: Math.round(cumulativeCash),
        lineOfCreditBalance: Math.round(lineOfCreditBalance)
      });

      // Move to next quarter
      currentDate.setMonth(currentDate.getMonth() + 3);
    }

    return data;
  }, [averagePrice, revenueStartDate, units2026, units2027, units2028, annualCost2026, annualCost2027, annualCost2028, toolingCost2026, toolingCost2027, toolingCost2028, cogsPerUnit, startingCash, baselineDate, burn2025Monthly, burn2026Annual, lineOfCreditLimit]);

  // Calculate key metrics per year
  const yearOneEndingCash = forecastData.length >= 4 ? forecastData[3].endingCash : 0;
  const yearTwoEndingCash = forecastData.length >= 8 ? forecastData[7].endingCash : 0;
  const yearThreeEndingCash = forecastData.length >= 12 ? forecastData[11].endingCash : 0;

  // Calculate profitability (revenue - COGS - total expenses, excluding line of credit)
  const yearOneProfitability = forecastData.slice(0, 4).reduce((sum, item) => sum + (item.revenue - item.cogs - item.totalExpenses), 0);
  const yearTwoProfitability = forecastData.slice(4, 8).reduce((sum, item) => sum + (item.revenue - item.cogs - item.totalExpenses), 0);
  const yearThreeProfitability = forecastData.slice(8, 12).reduce((sum, item) => sum + (item.revenue - item.cogs - item.totalExpenses), 0);

  // Find minimum cash reached
  const minCash = Math.min(...forecastData.map(item => item.endingCash));
  const minCashQuarter = forecastData.findIndex(item => item.endingCash === minCash) + 1;

  // Calculate advanced metrics
  const totalRevenue = forecastData.reduce((sum, item) => sum + item.revenue, 0);
  const totalUnits = forecastData.reduce((sum, item) => sum + item.units, 0);
  const totalCOGS = forecastData.reduce((sum, item) => sum + item.cogs, 0);
  const avgGrossMargin = totalRevenue > 0 ? ((totalRevenue - totalCOGS) / totalRevenue) * 100 : 0;
  const avgRevenuePerUnit = totalUnits > 0 ? totalRevenue / totalUnits : 0;

  // Break-even quarter (first quarter with positive cumulative profitability)
  let cumulativeProfit = 0;
  const breakEvenQuarter = forecastData.findIndex(item => {
    cumulativeProfit += (item.revenue - item.cogs - item.totalExpenses);
    return cumulativeProfit > 0;
  });

  // Cash runway (quarters until cash < 0)
  const cashRunoutQuarter = forecastData.findIndex(item => item.endingCash < 0);
  const cashRunway = cashRunoutQuarter >= 0 ? cashRunoutQuarter : 12;

  // Negative cash flow quarters
  const negativeCFQuarters = forecastData.filter(item => item.netCashFlow < 0).length;

  // YoY Growth
  const year1Revenue = forecastData.slice(0, 4).reduce((sum, item) => sum + item.revenue, 0);
  const year2Revenue = forecastData.slice(4, 8).reduce((sum, item) => sum + item.revenue, 0);
  const year3Revenue = forecastData.slice(8, 12).reduce((sum, item) => sum + item.revenue, 0);
  const yoyGrowthY2 = year1Revenue > 0 ? ((year2Revenue - year1Revenue) / year1Revenue) * 100 : 0;
  const yoyGrowthY3 = year2Revenue > 0 ? ((year3Revenue - year2Revenue) / year2Revenue) * 100 : 0;

  const year1Units = forecastData.slice(0, 4).reduce((sum, item) => sum + item.units, 0);
  const year2Units = forecastData.slice(4, 8).reduce((sum, item) => sum + item.units, 0);
  const year3Units = forecastData.slice(8, 12).reduce((sum, item) => sum + item.units, 0);
  const unitGrowthY2 = year1Units > 0 ? ((year2Units - year1Units) / year1Units) * 100 : 0;
  const unitGrowthY3 = year2Units > 0 ? ((year3Units - year2Units) / year2Units) * 100 : 0;

  // Total tooling investment
  const totalTooling = toolingCost2026 + toolingCost2027 + toolingCost2028;

  // Peak line of credit usage
  const peakLOCUsage = lineOfCreditLimit - Math.min(...forecastData.map(item => item.lineOfCreditBalance));
  const avgLOCUsage = (lineOfCreditLimit - (forecastData.reduce((sum, item) => sum + item.lineOfCreditBalance, 0) / 12));

  // Operating expense ratio (total opex / total revenue)
  const totalOpEx = forecastData.reduce((sum, item) => sum + item.operatingExpenses, 0);
  const opExRatio = totalRevenue > 0 ? (totalOpEx / totalRevenue) * 100 : 0;

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>The Looey Forecast</h1>
        <p className="subtitle">3-Year Financial Projection</p>
      </header>

      <div className="dashboard-content">
        <aside className="sidebar">
          <div className="controls-section">
        <h3 className="section-title">Saved Views</h3>
        <div className="views-actions">
          <select
            value={selectedView}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__new__') {
                const name = window.prompt('Enter a name for this view:');
                if (name && name.trim()) {
                  saveView(name);
                }
              } else if (value === '__load__') {
                loadViewFromJSON();
                e.target.value = ''; // Reset dropdown
              } else if (value) {
                loadView(value);
              }
            }}
            className="view-select-sidebar"
          >
            <option value="">Select or create view...</option>
            <option value="__new__">+ Create New View</option>
            <option value="__load__">📋 Load from JSON</option>
            {savedViews.map(view => (
              <option key={view.name} value={view.name}>
                {view.name}
              </option>
            ))}
          </select>
          {selectedView && selectedView !== 'Assumptions' && (
            <button
              onClick={() => deleteView(selectedView)}
              className="delete-btn-sidebar"
              title="Delete selected view"
            >
              🗑️
            </button>
          )}
        </div>

        <button
          onClick={handleExport}
          className="export-btn"
          title="Export screenshot and CSV"
        >
          📊 Export Forecast
        </button>

        <h3 className="section-title">Financial Assumptions</h3>

        <div className="controls-grid">

          <div className="control-card">
            <label className="input-label">COGS Per Unit</label>
            <div className="input-wrapper">
              <span className="input-prefix">$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={cogsPerUnit}
                onChange={(e) => setCogsPerUnit(Number(e.target.value))}
                className="text-input"
              />
            </div>
          </div>

          <div className="control-card">
            <label className="input-label">Unit Price</label>
            <div className="input-wrapper">
              <span className="input-prefix">$</span>
              <input
                type="number"
                min="1"
                step="1"
                value={averagePrice}
                onChange={(e) => setAveragePrice(Number(e.target.value))}
                className="text-input"
              />
            </div>
          </div>

          <div className="control-card">
            <label className="input-label">Revenue Start Date</label>
            <input
              type="date"
              value={revenueStartDate}
              onChange={(e) => setRevenueStartDate(e.target.value)}
              className="date-input"
            />
          </div>
        </div>

        <h3 className="section-title" style={{marginTop: '2rem'}}>Annual Tooling Costs</h3>

        <div className="controls-grid">
          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2026 Tooling</label>
              <span className="control-value">${(toolingCost2026 / 1000).toFixed(0)}k</span>
            </div>
            <input
              type="range"
              min="0"
              max="100000"
              step="5000"
              value={toolingCost2026}
              onChange={(e) => setToolingCost2026(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>$0</span>
              <span>$100k</span>
            </div>
          </div>

          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2027 Tooling</label>
              <span className="control-value">${(toolingCost2027 / 1000).toFixed(0)}k</span>
            </div>
            <input
              type="range"
              min="0"
              max="100000"
              step="5000"
              value={toolingCost2027}
              onChange={(e) => setToolingCost2027(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>$0</span>
              <span>$100k</span>
            </div>
          </div>

          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2028 Tooling</label>
              <span className="control-value">${(toolingCost2028 / 1000).toFixed(0)}k</span>
            </div>
            <input
              type="range"
              min="0"
              max="100000"
              step="5000"
              value={toolingCost2028}
              onChange={(e) => setToolingCost2028(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>$0</span>
              <span>$100k</span>
            </div>
          </div>
        </div>

        <h3 className="section-title" style={{marginTop: '2rem'}}>Annual Unit Sales</h3>

        <div className="controls-grid">
          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2026 Units</label>
              <span className="control-value">{units2026.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000"
              step="100"
              value={units2026}
              onChange={(e) => setUnits2026(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>0</span>
              <span>10,000</span>
            </div>
          </div>

          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2027 Units</label>
              <span className="control-value">{units2027.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000"
              step="100"
              value={units2027}
              onChange={(e) => setUnits2027(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>0</span>
              <span>10,000</span>
            </div>
          </div>

          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2028 Units</label>
              <span className="control-value">{units2028.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000"
              step="100"
              value={units2028}
              onChange={(e) => setUnits2028(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>0</span>
              <span>10,000</span>
            </div>
          </div>
        </div>

        <h3 className="section-title" style={{marginTop: '2rem'}}>Annual Operating Costs</h3>

        <div className="controls-grid">
          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2026 Costs</label>
              <span className="control-value">${(annualCost2026 / 1000).toFixed(0)}k</span>
            </div>
            <input
              type="range"
              min="50000"
              max="500000"
              step="10000"
              value={annualCost2026}
              onChange={(e) => setAnnualCost2026(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>$50k</span>
              <span>$500k</span>
            </div>
          </div>

          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2027 Costs</label>
              <span className="control-value">${(annualCost2027 / 1000).toFixed(0)}k</span>
            </div>
            <input
              type="range"
              min="50000"
              max="500000"
              step="10000"
              value={annualCost2027}
              onChange={(e) => setAnnualCost2027(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>$50k</span>
              <span>$500k</span>
            </div>
          </div>

          <div className="control-card">
            <div className="control-header">
              <label className="input-label">2028 Costs</label>
              <span className="control-value">${(annualCost2028 / 1000).toFixed(0)}k</span>
            </div>
            <input
              type="range"
              min="50000"
              max="500000"
              step="10000"
              value={annualCost2028}
              onChange={(e) => setAnnualCost2028(Number(e.target.value))}
              className="slider"
            />
            <div className="slider-labels">
              <span>$50k</span>
              <span>$500k</span>
            </div>
          </div>
        </div>
          </div>
        </aside>

        <main className="main-content">
          <div className="metrics-container">
            {/* First row - Ending Cash */}
            <div className="metrics-row">
              <div className={`metric-card ${yearOneEndingCash < 100000 ? 'metric-warning' : yearOneEndingCash < 50000 ? 'metric-danger' : ''}`}>
                <div className="metric-label">Year 1 Ending Cash</div>
                <div className="metric-value">${yearOneEndingCash.toLocaleString()}</div>
              </div>
              <div className={`metric-card ${yearTwoEndingCash < 100000 ? 'metric-warning' : yearTwoEndingCash < 50000 ? 'metric-danger' : ''}`}>
                <div className="metric-label">Year 2 Ending Cash</div>
                <div className="metric-value">${yearTwoEndingCash.toLocaleString()}</div>
              </div>
              <div className={`metric-card ${yearThreeEndingCash < 100000 ? 'metric-warning' : yearThreeEndingCash < 0 ? 'metric-danger' : ''}`}>
                <div className="metric-label">Year 3 Ending Cash</div>
                <div className="metric-value">${yearThreeEndingCash.toLocaleString()}</div>
              </div>
            </div>

            {/* Second row - Profitability */}
            <div className="metrics-row">
              <div className={`metric-card ${yearOneProfitability < 0 ? 'metric-danger' : ''}`}>
                <div className="metric-label">Year 1 Profitability</div>
                <div className="metric-value">${yearOneProfitability.toLocaleString()}</div>
              </div>
              <div className={`metric-card ${yearTwoProfitability < 0 ? 'metric-danger' : ''}`}>
                <div className="metric-label">Year 2 Profitability</div>
                <div className="metric-value">${yearTwoProfitability.toLocaleString()}</div>
              </div>
              <div className={`metric-card ${yearThreeProfitability < 0 ? 'metric-danger' : ''}`}>
                <div className="metric-label">Year 3 Profitability</div>
                <div className="metric-value">${yearThreeProfitability.toLocaleString()}</div>
              </div>
            </div>

            {minCash < 50000 && (
              <div className="metrics-row">
                <div className="metric-card metric-danger" style={{width: '100%'}}>
                  <div className="metric-label">⚠️ Minimum Cash Reached</div>
                  <div className="metric-value">${minCash.toLocaleString()}</div>
                </div>
              </div>
            )}
          </div>

      <div className="table-section">
        <h2>Quarterly Forecast Breakdown</h2>
        <div className="table-wrapper">
          <table className="forecast-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Start</th>
                <th>End</th>
                <th>Units</th>
                <th>Revenue</th>
                <th>COGS</th>
                <th>Gross Profit</th>
                <th>Expenses</th>
                <th>Net Cash Flow</th>
                <th>Ending Cash</th>
                <th>4Q Profitability</th>
                <th>Line of Credit</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {forecastData.map((row, index) => {
                const isDanger = row.endingCash < 50000;
                const isWarning = row.endingCash < 100000 && !isDanger;
                const locDanger = row.lineOfCreditBalance < 100000;
                const locWarning = row.lineOfCreditBalance < 200000 && !locDanger;

                // Calculate 4-quarter profitability (trailing 4 quarters)
                let fourQuarterProfit = null;
                if (index >= 3) {
                  fourQuarterProfit = forecastData
                    .slice(index - 3, index + 1)
                    .reduce((sum, item) => sum + item.netCashFlow, 0);
                }

                // Generate notes
                let notes = '';
                if (row.oneTimeExpenses > 0) {
                  notes = `Tooling: $${row.oneTimeExpenses.toLocaleString()}`;
                }

                return (
                  <tr key={index} className={isDanger ? 'row-danger' : isWarning ? 'row-warning' : ''}>
                    <td className="period-cell">{row.period}</td>
                    <td className="date-cell">{row.startDate}</td>
                    <td className="date-cell">{row.endDate}</td>
                    <td className="units-cell">{row.units.toLocaleString()}</td>
                    <td className="revenue-cell">${row.revenue.toLocaleString()}</td>
                    <td className="cogs-cell">${row.cogs.toLocaleString()}</td>
                    <td className="profit-cell">${row.grossProfit.toLocaleString()}</td>
                    <td className="expense-cell">${row.totalExpenses.toLocaleString()}</td>
                    <td className={`cashflow-cell ${row.netCashFlow < 0 ? 'negative' : 'positive'}`}>
                      ${row.netCashFlow.toLocaleString()}
                    </td>
                    <td className={`ending-cash-cell ${isDanger ? 'danger' : isWarning ? 'warning' : ''}`}>
                      ${row.endingCash.toLocaleString()}
                    </td>
                    <td className={`four-quarter-cell ${fourQuarterProfit !== null ? (fourQuarterProfit >= 0 ? 'positive' : 'negative') : ''}`}>
                      {fourQuarterProfit !== null ? `$${fourQuarterProfit.toLocaleString()}` : '-'}
                    </td>
                    <td className={`loc-cell ${locDanger ? 'danger' : locWarning ? 'warning' : ''}`}>
                      ${row.lineOfCreditBalance.toLocaleString()}
                    </td>
                    <td className="notes-cell">{notes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Advanced Metrics Dashboard */}
      <div className="advanced-metrics">
        <h2>Business Intelligence & Analytics</h2>

        {/* Key Performance Indicators */}
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-icon">💰</div>
            <div className="kpi-content">
              <div className="kpi-label">Total Revenue (3Y)</div>
              <div className="kpi-value">${totalRevenue.toLocaleString()}</div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">📦</div>
            <div className="kpi-content">
              <div className="kpi-label">Total Units Shipped</div>
              <div className="kpi-value">{totalUnits.toLocaleString()}</div>
            </div>
          </div>

          <div className={`kpi-card ${avgGrossMargin < 30 ? 'kpi-warning' : ''}`}>
            <div className="kpi-icon">📊</div>
            <div className="kpi-content">
              <div className="kpi-label">Average Gross Margin</div>
              <div className="kpi-value">{avgGrossMargin.toFixed(1)}%</div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">💵</div>
            <div className="kpi-content">
              <div className="kpi-label">Avg Revenue/Unit</div>
              <div className="kpi-value">${avgRevenuePerUnit.toFixed(0)}</div>
            </div>
          </div>

          <div className={`kpi-card ${breakEvenQuarter < 0 ? 'kpi-danger' : 'kpi-success'}`}>
            <div className="kpi-icon">🎯</div>
            <div className="kpi-content">
              <div className="kpi-label">Break-Even Quarter</div>
              <div className="kpi-value">{breakEvenQuarter >= 0 ? `Q${breakEvenQuarter + 1}` : 'Not Reached'}</div>
            </div>
          </div>

          <div className={`kpi-card ${minCash < 100000 ? 'kpi-warning' : ''}`}>
            <div className="kpi-icon">⚠️</div>
            <div className="kpi-content">
              <div className="kpi-label">Min Cash (Q{minCashQuarter})</div>
              <div className="kpi-value">${minCash.toLocaleString()}</div>
            </div>
          </div>

          <div className={`kpi-card ${cashRunway < 12 ? 'kpi-danger' : 'kpi-success'}`}>
            <div className="kpi-icon">⏱️</div>
            <div className="kpi-content">
              <div className="kpi-label">Cash Runway</div>
              <div className="kpi-value">{cashRunway >= 12 ? '12+ Qtrs' : `${cashRunway} Qtrs`}</div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon">🏦</div>
            <div className="kpi-content">
              <div className="kpi-label">Peak LOC Usage</div>
              <div className="kpi-value">${peakLOCUsage.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-grid">
          {/* Revenue & Profitability Chart */}
          <div className="chart-card">
            <h3>Revenue & Cash Flow Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Area type="monotone" dataKey="revenue" stackId="1" stroke="#4ecdc4" fill="#4ecdc4" name="Revenue" />
                <Area type="monotone" dataKey="netCashFlow" stackId="2" stroke="#1a3a52" fill="#1a3a52" name="Net Cash Flow" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Units & COGS Chart */}
          <div className="chart-card">
            <h3>Unit Economics</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="units" fill="#4ecdc4" name="Units" />
                <Bar yAxisId="right" dataKey="grossProfit" fill="#1a3a52" name="Gross Profit ($)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cash Position Chart */}
          <div className="chart-card chart-wide">
            <h3>Cash Position & Line of Credit</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="endingCash" stroke="#4ecdc4" strokeWidth={3} name="Ending Cash" />
                <Line type="monotone" dataKey="lineOfCreditBalance" stroke="#f0ad4e" strokeWidth={2} strokeDasharray="5 5" name="LOC Available" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Growth & Efficiency Metrics */}
        <div className="metrics-breakdown">
          <div className="breakdown-section">
            <h3>📈 Year-over-Year Growth</h3>
            <div className="breakdown-grid">
              <div className="breakdown-item">
                <span className="breakdown-label">Year 2 Revenue Growth</span>
                <span className={`breakdown-value ${yoyGrowthY2 >= 0 ? 'positive' : 'negative'}`}>
                  {yoyGrowthY2 >= 0 ? '+' : ''}{yoyGrowthY2.toFixed(1)}%
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Year 3 Revenue Growth</span>
                <span className={`breakdown-value ${yoyGrowthY3 >= 0 ? 'positive' : 'negative'}`}>
                  {yoyGrowthY3 >= 0 ? '+' : ''}{yoyGrowthY3.toFixed(1)}%
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Year 2 Unit Growth</span>
                <span className={`breakdown-value ${unitGrowthY2 >= 0 ? 'positive' : 'negative'}`}>
                  {unitGrowthY2 >= 0 ? '+' : ''}{unitGrowthY2.toFixed(1)}%
                </span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Year 3 Unit Growth</span>
                <span className={`breakdown-value ${unitGrowthY3 >= 0 ? 'positive' : 'negative'}`}>
                  {unitGrowthY3 >= 0 ? '+' : ''}{unitGrowthY3.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="breakdown-section">
            <h3>⚙️ Operational Efficiency</h3>
            <div className="breakdown-grid">
              <div className="breakdown-item">
                <span className="breakdown-label">COGS as % of Revenue</span>
                <span className="breakdown-value">{totalRevenue > 0 ? ((totalCOGS / totalRevenue) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">OpEx Ratio</span>
                <span className="breakdown-value">{opExRatio.toFixed(1)}%</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Total Tooling Investment</span>
                <span className="breakdown-value">${totalTooling.toLocaleString()}</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Negative CF Quarters</span>
                <span className={`breakdown-value ${negativeCFQuarters > 6 ? 'negative' : 'neutral'}`}>
                  {negativeCFQuarters} of 12
                </span>
              </div>
            </div>
          </div>

          <div className="breakdown-section">
            <h3>💳 Financing Metrics</h3>
            <div className="breakdown-grid">
              <div className="breakdown-item">
                <span className="breakdown-label">Peak LOC Usage</span>
                <span className="breakdown-value">${peakLOCUsage.toLocaleString()}</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Average LOC Usage</span>
                <span className="breakdown-value">${avgLOCUsage.toLocaleString()}</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">LOC Utilization</span>
                <span className="breakdown-value">{((peakLOCUsage / lineOfCreditLimit) * 100).toFixed(1)}%</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">Return on Tooling</span>
                <span className="breakdown-value">{totalTooling > 0 ? ((totalRevenue / totalTooling) * 100).toFixed(0) : 0}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

        </main>
      </div>
    </div>
  );
};

export default ForecastDashboard;
